import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GetRandomTokenThunk, GetDrawThunk } from "@/redux/features/actions/draw";
import { useAppSelector, useAppDispatch } from "@/redux/hook";
import CheckRole from "@/components/etc/CheckRoles";
import { BackIcon } from "@/assets/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import RouletteSinglePage from "./single";
import useSound from "use-sound";
import countdownSound from "@/assets/54321-35890.mp3";
import spinningSound from "@/assets/spinning-reel-27903.mp3";
import confettiSound from "@/assets/goodresult-82807.mp3";
import formatDateToLongForm from "@/helpers/format";

const MAX_VISIBLE_BOXES = 7;

export default function UnplayedDraws() {
  const [selectedPrize, setSelectedPrize] = useState<any>(null);
  const [fetched, setFetched] = useState(false);
  const [load, setLoad] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [winner, setWinner] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = useNavigate();
  const { list }: any = useAppSelector((s) => s.draw);
  const dispatch = useAppDispatch();

  const filter = {
    isPlayed: false,
  };

  const getData = useCallback(async () => {
    try {
      await dispatch(GetDrawThunk(filter)).unwrap();
      setFetched(true);
    } catch (error) {
      console.error("Error fetching draw data:", error);
    }
  }, [dispatch]);

  useLayoutEffect(() => {
    if (!fetched) getData();
  }, [getData, fetched]);

  const emptyBoxesCount = Math.max(0, MAX_VISIBLE_BOXES - list.length);

  useEffect(() => {
    if (list.length > 0 && !selectedPrize) {
      setSelectedPrize(list[0]);
    }
  }, [list, selectedPrize]);

  console.log(list);

  const [playSpinning, { stop: stopSpinning }] = useSound(spinningSound, {
    volume: 0.5,
    interrupt: true,
  });
  const [playCountdown] = useSound(countdownSound, { volume: 0.5 });
  const [playConfetti] = useSound(confettiSound, { volume: 0.5 });

  const getTokens = useCallback(async () => {
    if (!selectedPrize) return;
    try {
      setLoad(true);
      const winnerData = await dispatch(GetRandomTokenThunk(selectedPrize?.drawId)).unwrap();

      // Start countdown
      setCountdown(10);
      playCountdown(); // Play countdown sound

      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);

            // When countdown finishes, start spinning sound
            playSpinning(); // Start spinning sound

            setTimeout(() => {
              // Stop spinning sound after 5 seconds
              stopSpinning();

              // Play confetti sound after spinning stops
              playConfetti(); // Play confetti sound
            }, 5000); // Stop spinning after 5 seconds

            setWinner(winnerData);
            setModalOpen(true);

            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast(error?.response?.data?.message || "Something went wrong", {
        type: "error",
        hideProgressBar: true,
      });
    } finally {
      setLoad(false);
    }
  }, [dispatch, selectedPrize, playCountdown, playSpinning, playConfetti, stopSpinning]);

  return (
    <div className="bg-orange-100 flex flex-col justify-center items-center p-6 ">
      <div className="w-full max-w-4xl bg-orange-500 py-7 rounded-md text-center shadow-md">
        <h1 className="text-white font-bold text-2xl">WIN BIG *474#</h1>
      </div>

      <div className="bg-orange-500 w-full max-w-4xl h-[40vh] mt-6 rounded-md uppercase shadow-lg p-10 flex flex-col justify-center items-center">
        <div className="w-fit p-10 flex flex-col justify-center items-center bg-orange-400">
          <h2 className="text-white text-center text-2xl font-bold">
            YOU ARE Drawing <br />
            {selectedPrize && (
              <span className="font-bold text-3xl mt-4"> {selectedPrize.product.productName}</span>
            )}
          </h2>

          {countdown > 0 ? (
            <div className="mt-6 text-7xl text-white font-bold">{countdown}</div>
          ) : (
            <button
              onClick={getTokens}
              disabled={load}
              className={`mt-6 border-none bg-white text-orange-600 font-semibold uppercase text-lg py-2 px-6 rounded-md shadow-md transition-all ${
                load ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-600 hover:text-white"
              }`}
            >
              {"START DRAWING"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-4 mt-6">
        <button
          className="p-2 bg-orange-400 rounded-full shadow-md hover:bg-orange-500 disabled:opacity-50"
          disabled={list.length <= 7}
        >
          <ChevronLeft className="text-white" />
        </button>

        <div className="grid grid-cols-9 gap-4">
          {Array.from({ length: Math.floor(emptyBoxesCount / 2) }).map((_, index) => (
            <div
              key={`empty-left-${index}`}
              className="p-2 bg-orange-200 w-24 h-32 rounded-md opacity-50"
            />
          ))}

{[...list]
  .slice() // Creates a new copy of the array
  .sort((a: any, b: any) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime())
  .map((prize: any) => (
    <div
      key={prize.drawId}
      className={`p-2 w-24 h-32 flex flex-col items-center justify-center rounded-md cursor-pointer bg-orange-500 transition-all ${
        selectedPrize?.drawId === prize.drawId
          ? "border-4 border-white shadow-lg transform scale-110"
          : "opacity-70 hover:opacity-100"
      }`}
      onClick={() => setSelectedPrize(prize)}
    >
      <img
        src={prize.product.productPicture}
        alt={prize.product.productName}
        className="h-16 w-full mx-auto"
      />
      <p className="text-white text-xs text-center">{prize.product.productName}</p>
      <p className="text-white text-[10px] mt-1">{formatDateToLongForm(prize?.endDate)}</p>
    </div>
  ))}

        </div>

        <button
          className="p-2 bg-orange-400 rounded-full shadow-md hover:bg-orange-500 disabled:opacity-50"
          disabled={list.length <= 7} 
        >
          <ChevronRight className="text-white" />
        </button>
      </div>

      <RouletteSinglePage
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        draw={selectedPrize}
        winners={winner}
      />

      <div className="mt-6 flex space-x-4">
        <CheckRole permission={"view_dashboard"}>
          <div
            onClick={() => navigate(-1)}
            className="bg-white border-gray-50 p-4 border-2 border-solid rounded-md cursor-pointer"
          >
            <BackIcon className="w-6 text-light-grey" /> <p className="hidden">Go Back</p>
          </div>
        </CheckRole>
      </div>
    </div>
  );
}
